import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, CalendarCheck, MessageSquare, FileText, ClipboardCheck, Printer, Smartphone } from 'lucide-react';
import { Gate } from '../../AccessStudio';
import GradesReportModal from './GradesReportModal';
import ConductReportModal from './ConductReportModal';
import StudentRemarksModal from './StudentRemarksModal';
import BulletinModal from './BulletinModal';
import CarnetModal from './CarnetModal';
import SendToAppModal from './SendToAppModal';
import AnalyticsView from './AnalyticsView';
import useScopedClasses from '../useScopedClasses';
import { buildBulletinHTML, buildCarnetHTML, printPages } from './printBuilders';

// Pick students → open any report. Checkboxes select 1, 10 or the
// whole class for BATCH printing (one print job, one page per child);
// the Remarks/Bulletin/Carnet modals carry prev/next arrows so you can
// walk the class without reopening them.
const ReportsView = ({ apiCall, year, showError, showSuccess }) => {
  const [mode, setMode] = useState('students'); // students | analytics
  const { visibleClasses, className, setClassName } = useScopedClasses(apiCall, year);
  const [sections, setSections] = useState([]);
  const [sectionCode, setSectionCode] = useState('');
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState([]);      // StudentIDs (batch print)
  const [printing, setPrinting] = useState(null);    // progress text
  const [gradesFor, setGradesFor] = useState(null);
  const [conductFor, setConductFor] = useState(null);
  const [remarksAt, setRemarksAt] = useState(null);  // index into students
  const [bulletinAt, setBulletinAt] = useState(null);
  const [carnetAt, setCarnetAt] = useState(null);
  const [sendToApp, setSendToApp] = useState(false);


  useEffect(() => {
    if (!className) return;
    apiCall(`/academic/sections?academicYear=${year}&className=${encodeURIComponent(className)}`)
      .then(s => setSections(s.filter(x => x.IsActive))).catch(() => setSections([]));
    setSectionCode('');
  }, [apiCall, year, className]);

  const load = useCallback(async () => {
    if (!className) return;
    try {
      let url = `/academic/roster?academicYear=${year}&className=${encodeURIComponent(className)}`;
      if (sectionCode) url += `&section=${encodeURIComponent(sectionCode)}`;
      setStudents(await apiCall(url));
      setSelected([]);
    } catch (err) { showError(err.message); }
  }, [apiCall, year, className, sectionCode, showError]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  const toggleAll = () =>
    setSelected(sel => sel.length === students.length ? [] : students.map(s => s.StudentID));

  // One print job for the whole selection, one page per student
  const batchPrint = async (kind) => {
    const picked = students.filter(s => selected.includes(s.StudentID));
    if (!picked.length) { showError('Select students first'); return; }
    const endpoint = kind === 'bulletin' ? 'bulletin' : 'carnet';
    const build = kind === 'bulletin' ? buildBulletinHTML : buildCarnetHTML;
    try {
      const pages = [];
      for (let i = 0; i < picked.length; i++) {
        setPrinting(`${kind === 'bulletin' ? 'Bulletins' : 'Carnets'} ${i + 1}/${picked.length}…`);
        const data = await apiCall(`/academic/reports/${endpoint}?studentId=${encodeURIComponent(picked[i].StudentID)}&academicYear=${year}`);
        pages.push(build(data, year));
      }
      printPages(`${kind === 'bulletin' ? 'Bulletins' : 'Carnets'} ${className} · ${year}`, pages);
    } catch (err) { showError(err.message); }
    finally { setPrinting(null); }
  };

  return (
    <div className="space-y-4">
      {/* Student documents vs class analytics */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button onClick={() => setMode('students')}
          className={`px-3 py-1.5 rounded-md text-sm font-medium ${mode === 'students' ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>
          Student Reports
        </button>
        <Gate adminOnly label="Class Analytics">
          <button onClick={() => setMode('analytics')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${mode === 'analytics' ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>
            Analytics
          </button>
        </Gate>
      </div>

      {mode === 'analytics' ? (
        <AnalyticsView apiCall={apiCall} year={year} showError={showError} />
      ) : (
      <>
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
        <select value={className} onChange={e => setClassName(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {visibleClasses.length === 0 && <option value="">No classes</option>}
          {visibleClasses.map(cl => <option key={cl.ClassName} value={cl.ClassName}>{cl.ClassName}</option>)}
        </select>
        <select value={sectionCode} onChange={e => setSectionCode(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All sections</option>
          {sections.map(s => <option key={s.SectionID} value={s.SectionCode}>{className} {s.SectionCode}</option>)}
        </select>
        <span className="text-sm text-gray-500">{students.length} students</span>
        <div className="flex-1" />
        {/* Batch printing for the selection */}
        <Gate adminOnly label="Batch Print Bulletins">
          <button
            onClick={() => batchPrint('bulletin')}
            disabled={!selected.length || !!printing}
            className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Printer size={14} /> {printing || `Print Bulletins (${selected.length})`}
          </button>
        </Gate>
        <Gate adminOnly label="Batch Print Carnets">
          <button
            onClick={() => batchPrint('carnet')}
            disabled={!selected.length || !!printing}
            className="px-3 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Printer size={14} /> Print Carnets ({selected.length})
          </button>
        </Gate>
        <Gate adminOnly label="Send Report Cards to App">
          <button
            onClick={() => setSendToApp(true)}
            className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
          >
            <Smartphone size={14} /> Send to App
          </button>
        </Gate>
      </div>

      <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
        {/* Select-all header */}
        {students.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 text-xs text-gray-500 uppercase">
            <input type="checkbox"
              checked={students.length > 0 && selected.length === students.length}
              onChange={toggleAll} />
            <span>{selected.length ? `${selected.length} selected` : 'Select all'}</span>
          </div>
        )}
        {students.map((s, i) => (
          <div key={s.StudentID} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 text-sm">
            <label className="flex items-center gap-3 cursor-pointer min-w-0">
              <input type="checkbox" checked={selected.includes(s.StudentID)} onChange={() => toggle(s.StudentID)} />
              <span className="font-medium text-gray-800 truncate">
                {s.LastName} {s.FirstName}
                {s.Section && <span className="ml-2 text-xs text-gray-400">{className} {s.Section}</span>}
              </span>
            </label>
            <div className="flex gap-2 flex-shrink-0">
              <Gate permission="academic.reports.grades" label="Grades Report">
                <button
                  onClick={() => setGradesFor(s)}
                  className="px-3 py-1 text-xs border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 flex items-center gap-1"
                >
                  <BookOpen size={13} /> Grades
                </button>
              </Gate>
              <Gate permission="academic.reports.conduct" label="Conduct Report">
                <button
                  onClick={() => setConductFor(s)}
                  className="px-3 py-1 text-xs border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 flex items-center gap-1"
                >
                  <CalendarCheck size={13} /> Conduct
                </button>
              </Gate>
              <Gate adminOnly label="Student Remarks">
                <button
                  onClick={() => setRemarksAt(i)}
                  className="px-3 py-1 text-xs border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 flex items-center gap-1"
                >
                  <MessageSquare size={13} /> Remarks
                </button>
              </Gate>
              <Gate adminOnly label="Bulletin Scolaire">
                <button
                  onClick={() => setBulletinAt(i)}
                  className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1"
                >
                  <FileText size={13} /> Bulletin
                </button>
              </Gate>
              <Gate adminOnly label="Carnet de Compétences">
                <button
                  onClick={() => setCarnetAt(i)}
                  className="px-3 py-1 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1"
                >
                  <ClipboardCheck size={13} /> Carnet
                </button>
              </Gate>
            </div>
          </div>
        ))}
        {students.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No students.</p>}
      </div>

      {gradesFor && (
        <GradesReportModal apiCall={apiCall} year={year} student={gradesFor}
          onClose={() => setGradesFor(null)} showError={showError} />
      )}
      {conductFor && (
        <ConductReportModal apiCall={apiCall} year={year} student={conductFor}
          onClose={() => setConductFor(null)} showError={showError} />
      )}
      {remarksAt !== null && (
        <StudentRemarksModal apiCall={apiCall} year={year} students={students} startIndex={remarksAt}
          onClose={() => setRemarksAt(null)} showSuccess={showSuccess} showError={showError} />
      )}
      {bulletinAt !== null && (
        <BulletinModal apiCall={apiCall} year={year} students={students} startIndex={bulletinAt}
          onClose={() => setBulletinAt(null)} showError={showError} />
      )}
      {carnetAt !== null && (
        <CarnetModal apiCall={apiCall} year={year} students={students} startIndex={carnetAt}
          onClose={() => setCarnetAt(null)} showError={showError} />
      )}
      {sendToApp && (
        <SendToAppModal apiCall={apiCall} year={year} className={className}
          onClose={() => setSendToApp(false)} showSuccess={showSuccess} showError={showError} />
      )}
      </>
      )}
    </div>
  );
};

export default ReportsView;
